import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Home } from "lucide-react";
import api from "../lib/api";

export default function PaystackReturn() {
  const [sp] = useSearchParams();
  const reference = sp.get("reference") || sp.get("trxref") || "";
  const [state, setState] = useState({ status: "polling", payment_status: "pending", donor_serial: "" });
  const [tries, setTries] = useState(0);
  const nav = useNavigate();

  useEffect(() => { document.title = "Confirming your card payment | Dutsen Kauna Foundation"; }, []);
  useEffect(() => {
    if (state.payment_status === "paid" && state.donor_serial && state.thank_you_token) nav(`/thank-you/${state.donor_serial}?t=${state.thank_you_token}&receipt=held`, { replace: true });
  }, [state, nav]);
  useEffect(() => {
    if (!reference) return;
    let alive = true;
    api.get(`/payments/paystack/status/${encodeURIComponent(reference)}`).then(({ data }) => {
      if (!alive) return;
      setState(data);
      if (data.payment_status !== "paid" && !["failed", "abandoned", "reversed"].includes(data.status) && tries < 12) setTimeout(() => setTries((t) => t + 1), 2500);
    }).catch(() => { if (alive) setState((s) => ({ ...s, status: "error" })); });
    return () => { alive = false; };
  }, [reference, tries]);

  const failed = ["failed", "abandoned", "reversed", "error"].includes(state.status) || tries >= 12;
  return (
    <div data-testid="paystack-return-page" className="bg-[#f8f4e8] min-h-[70vh] py-16">
      <div className="dk-container max-w-2xl">
        <div className="bg-white rounded-2xl p-10 text-center border border-[#d6aa43]/20">
          {!reference || failed ? (
            <>
              <XCircle className="text-red-500 mx-auto mb-4" size={56} />
              <h1 className="font-serif text-3xl text-[#0a0d12] font-bold mb-3">We could not confirm this payment</h1>
              <p className="text-[#0a0d12]/75 mb-6" data-testid="paystack-failed">{reference ? `Paystack has not reported this payment as successful yet (reference ${reference}). If money left your account, please contact us with this reference — nothing is lost.` : "No payment reference was found in the link."}</p>
              <div className="flex flex-wrap gap-3 justify-center"><Link to="/donate" className="dk-btn-primary">Try again</Link><Link to="/contact" className="dk-btn-outline !text-[#155e3f] !border-[#155e3f]">Contact us</Link></div>
            </>
          ) : (
            <>
              <Clock className="text-[#d6aa43] mx-auto mb-4 animate-pulse" size={56} />
              <h1 className="font-serif text-3xl text-[#0a0d12] font-bold mb-3">Confirming your payment…</h1>
              <p className="text-[#0a0d12]/75" data-testid="paystack-polling">We are checking with Paystack. This usually takes a few seconds — please keep this page open.</p>
              {state.donor_serial && <p className="text-xs text-[#0a0d12]/60 mt-4 font-mono">Donor reference {state.donor_serial}</p>}
            </>
          )}
          {state.payment_status === "paid" && <p className="mt-6 inline-flex items-center gap-2 text-green-700 font-semibold"><CheckCircle2 size={16} /> Paid — taking you to your thank-you page</p>}
          <div className="mt-8"><Link to="/" className="text-xs underline text-[#0a0d12]/60 inline-flex items-center gap-1"><Home size={12} /> Home</Link></div>
        </div>
      </div>
    </div>
  );
}
